/**
 * Recently Viewed Products
 * ─────────────────────────────────────────────────────────────────
 * 1. Saves the current product handle to localStorage on every visit.
 * 2. On the next product page, fetches previously-visited products
 *    via /products/{handle}.json and builds card HTML.
 * 3. Injects slides into the pre-rendered carousel shell and reveals it.
 * 4. Wires up prev/next arrow buttons for scrolling.
 */

const STORAGE_KEY = 'trovelle_recently_viewed';
const MAX_STORED  = 10;
const MAX_DISPLAY = 8;

/* ── localStorage ──────────────────────────────────────────────── */

function getStoredHandles() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
  catch { return []; }
}

function saveHandle(handle) {
  let list = getStoredHandles().filter(h => h !== handle);
  list.unshift(handle);
  list = list.slice(0, MAX_STORED);
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch { /* ignore */ }
}

/* ── Product JSON fetch ─────────────────────────────────────────── */

async function fetchProduct(handle) {
  try {
    const res = await fetch(`/products/${handle}.json`);
    if (!res.ok) return null;
    const { product } = await res.json();
    return product ?? null;
  } catch { return null; }
}

/* ── Helpers ────────────────────────────────────────────────────── */

function esc(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Append Shopify CDN size suffix: image.jpg → image_600x.jpg */
function resizeImg(src, size = '600x') {
  return (src || '').replace(/(\.[a-zA-Z]+)(\?|$)/, `_${size}$1$2`);
}

/** "5999.00" → "₹5,999" */
function formatPrice(str) {
  const n = parseFloat(str);
  return isNaN(n) ? '' : '₹' + n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

/* ── Card HTML ──────────────────────────────────────────────────── */

function buildSlide(product) {
  const imgSrc  = resizeImg(product.image?.src ?? product.images?.[0]?.src ?? '');
  const title   = esc(product.title);
  const url     = `/products/${product.handle}`;
  const variant = product.variants?.[0];
  const price   = variant ? formatPrice(variant.price) : '';
  const compare = variant?.compare_at_price ? formatPrice(variant.compare_at_price) : '';
  const onSale  = compare && parseFloat(variant.compare_at_price) > parseFloat(variant.price);

  const imgHtml = imgSrc
    ? `<img src="${imgSrc}" alt="${title}" loading="lazy" width="300" height="400">`
    : `<div style="width:100%;height:100%;background:#f0f0f0"></div>`;

  return `
    <div class="rv-carousel__slide">
      <a class="rv-card" href="${url}" aria-label="${title}">
        <div class="rv-card__image">${imgHtml}</div>
        <div class="rv-card__info">
          <p class="rv-card__title">${title}</p>
          <div class="rv-card__price">
            <span class="rv-card__price-current">${price}</span>
            ${onSale ? `<span class="rv-card__price-compare">${compare}</span>` : ''}
          </div>
        </div>
      </a>
    </div>
  `;
}

/* ── Arrow wiring ────────────────────────────────────────────────── */

function wireArrows(track, prevBtn, nextBtn) {
  function slideWidth() {
    const slide = track.querySelector('.rv-carousel__slide');
    if (!slide) return 300;
    const gap = parseFloat(getComputedStyle(track).gap) || 12;
    return slide.offsetWidth + gap;
  }

  function updateVisibility() {
    const atStart = track.scrollLeft <= 4;
    const atEnd   = track.scrollLeft + track.offsetWidth >= track.scrollWidth - 4;
    prevBtn.style.display = atStart ? 'none' : '';
    nextBtn.style.display = atEnd   ? 'none' : '';
  }

  prevBtn.addEventListener('click', () => {
    track.scrollBy({ left: -slideWidth() * 2, behavior: 'smooth' });
  });
  nextBtn.addEventListener('click', () => {
    track.scrollBy({ left:  slideWidth() * 2, behavior: 'smooth' });
  });

  track.addEventListener('scroll', updateVisibility, { passive: true });
  updateVisibility();
}

/* ── Main ───────────────────────────────────────────────────────── */

async function initRecentlyViewed() {
  const section = document.querySelector('[data-section-id][class*="recently-viewed-products"]');
  if (!section) return;

  const id    = section.dataset.sectionId;
  const track = document.getElementById(`rv-carousel-track-${id}`);
  const prev  = document.getElementById(`rv-prev-${id}`);
  const next  = document.getElementById(`rv-next-${id}`);
  if (!track) return;

  // Save current product
  const current = window.__trovelle_currentProduct;
  if (current?.handle) saveHandle(current.handle);

  // Build display list (exclude current product)
  const handles = getStoredHandles()
    .filter(h => h !== current?.handle)
    .slice(0, MAX_DISPLAY);

  if (handles.length === 0) return;

  // Fetch all in parallel
  const products   = await Promise.all(handles.map(fetchProduct));
  const validSlides = products.filter(Boolean).map(buildSlide);

  if (validSlides.length === 0) return;

  // Replace skeletons with real slides
  track.innerHTML = validSlides.join('');

  // Wire arrows if they exist
  if (prev && next) wireArrows(track, prev, next);

  // Reveal section
  section.style.display = '';
}

/* ── Boot ───────────────────────────────────────────────────────── */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRecentlyViewed);
} else {
  initRecentlyViewed();
}
